/**
 * TransactionalMessageDetailPage.jsx — Single-message timeline + raw payload.
 * Backend: GET /messages/whatsapp/:messageId
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Descriptions,
  Tag,
  Spin,
  Button,
  Space,
  Alert,
  Tabs,
  message as antdMessage,
  Grid,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import transactionalApi from '../../../../../api/transactional.api';
import MessageTimeline from '../../../../../components/audience/MessageTimeline';

export default function TransactionalMessageDetailPage() {
  const { messageId } = useParams();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await transactionalApi.getMessage(messageId);
      const entity = res.data?.entity || res.data?.data || res.data;
      setData(entity?.message || entity);
    } catch (err) {
      console.error('Failed to load message', err);
      setError(err.response?.data?.message || 'Could not load message details.');
      antdMessage.error('Could not load message details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [messageId]);

  if (loading && !data) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }

  const isMobile = !screens.md;
  const codeStyle = { fontSize: 12, wordBreak: 'break-all', whiteSpace: 'pre-wrap' };

  return (
    <div style={{ padding: isMobile ? '10px 12px' : '16px 24px', maxWidth: '100%', overflowX: 'hidden' }}>
      <Space style={{ marginBottom: 8 }}>
        <Button size={isMobile ? 'small' : 'middle'} icon={<ArrowLeftOutlined />} onClick={() => navigate('/dashboard/transactional-messages')}>
          Back
        </Button>
        <Button size={isMobile ? 'small' : 'middle'} icon={<ReloadOutlined />} onClick={fetchData} loading={loading}>
          Refresh
        </Button>
      </Space>

      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} showIcon />}

      {data && (
        <>
          <Card
            title="Message Details"
            size={isMobile ? 'small' : 'default'}
            styles={{ header: { padding: isMobile ? '8px 12px' : '10px 16px', minHeight: 'auto' }, body: { padding: isMobile ? 8 : 12 } }}
            style={{ marginBottom: 10 }}
          >
            <Descriptions column={isMobile ? 1 : 2} bordered size="small">
              <Descriptions.Item label="Message ID" span={isMobile ? 1 : 2}>
                <code style={codeStyle}>{data.message_id}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Client Ref">
                <span style={{ wordBreak: 'break-all' }}>{data.client_ref || '—'}</span>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={
                  data.status === 'delivered' ? 'green' :
                  data.status === 'read' ? 'cyan' :
                  data.status === 'failed' ? 'red' :
                  data.status === 'sent' ? 'blue' : 'default'
                }>{data.status || 'unknown'}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Recipient (hashed)">
                <code style={codeStyle}>{data.to_hash || '—'}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Recipient (masked)">
                <code style={codeStyle}>{data.to_masked || '—'}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Template">{data.template_name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Language">{data.language || '—'}</Descriptions.Item>
              <Descriptions.Item label="Created At">
                {data.created_at ? dayjs(data.created_at).format('DD MMM YYYY HH:mm:ss') : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Updated At">
                {data.updated_at ? dayjs(data.updated_at).format('DD MMM YYYY HH:mm:ss') : '—'}
              </Descriptions.Item>
              {data.error_code && (
                <Descriptions.Item label="Error" span={isMobile ? 1 : 2}>
                  <Tag color="red">{data.error_code}</Tag> {data.error_message}
                </Descriptions.Item>
              )}
            </Descriptions>
          </Card>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 10 : 16,
              alignItems: 'start',
            }}
          >
            <Card
              title="Delivery Timeline"
              size={isMobile ? 'small' : 'default'}
              styles={{ header: { padding: isMobile ? '8px 12px' : '10px 16px', minHeight: 'auto' }, body: { padding: isMobile ? 8 : 12 } }}
              style={{ minWidth: 0, overflow: 'hidden' }}
            >
              <MessageTimeline events={data.events || []} />
            </Card>

            <Card
              title="Raw Payload"
              size={isMobile ? 'small' : 'default'}
              styles={{ header: { padding: isMobile ? '8px 12px' : '10px 16px', minHeight: 'auto' }, body: { padding: isMobile ? 8 : 12 } }}
              style={{ minWidth: 0, overflow: 'hidden' }}
            >
              <Tabs
                items={[
                  {
                    key: 'request',
                    label: 'Request',
                    children: (
                      <pre style={{ background: '#f5f5f5', padding: 10, borderRadius: 6, overflowX: 'auto', fontSize: 11, maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {JSON.stringify(data.request_payload || {}, null, 2)}
                      </pre>
                    ),
                  },
                  {
                    key: 'response',
                    label: 'Response',
                    children: (
                      <pre style={{ background: '#f5f5f5', padding: 10, borderRadius: 6, overflowX: 'auto', fontSize: 11, maxWidth: '100%', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                        {JSON.stringify(data.provider_response || {}, null, 2)}
                      </pre>
                    ),
                  },
                ]}
              />
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
